import { useState, useMemo } from "react";
import { AppContainer } from "@/components/layout/app-container";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { MonthlyCalendarGrid } from "@/components/calendar/monthly-calendar-grid";
import { CalendarNavigation } from "@/components/calendar/calendar-navigation";
import { AddEventDialog } from "@/components/calendar/add-event-dialog";
import { EditEventDialog } from "@/components/calendar/edit-event-dialog";
import CalendarWidget from "@/components/dashboard/calendar-widget";
import { ICalendarEvent } from "@/interfaces/dashboard.interface";
import { mockCalendarEvents } from "@/services/calendar/mock-data";

const monthNames = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

export function Calendario() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<ICalendarEvent[]>(mockCalendarEvents);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null);

  const currentMonth = monthNames[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  // Navigation handlers
  const handlePrevMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Event handlers
  const handleAddEvent = (event: Partial<ICalendarEvent>) => {
    setEvents((prev) => [...prev, event as ICalendarEvent]);
  };

  const handleUpdateEvent = (updatedEvent: ICalendarEvent) => {
    setEvents((prev) => prev.map((e) => (e.id === updatedEvent.id ? updatedEvent : e)));
  };

  const handleDeleteEvent = (eventId: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
  };

  const handleEventClick = (event: ICalendarEvent) => {
    setSelectedEvent(event);
    setEditDialogOpen(true);
  };

  const handleEditCalendar = () => {
    // Placeholder for edit calendar functionality
    console.log("Edit calendar clicked");
  };

  // Filter upcoming events for the widget
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return events
      .filter((event) => {
        const eventDate = new Date(event.date);
        return eventDate >= today;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [events]);

  return (
    <AppContainer
      title="Calendário"
      description="Texto de Apoio da página"
      button={
        <div className="flex gap-[10px]">
          <Button
            onClick={handleEditCalendar}
            variant="outline"
            className="h-[50px] border-[#7846FF] px-6 text-[#F5F2FF]"
          >
            <span className="text-[18px] font-bold">Editar Calendário</span>
          </Button>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="h-[50px] bg-[#7846FF] px-6 text-[#F5F2FF] hover:bg-[#6635E6]"
          >
            <Plus className="size-6" />
            <span className="text-[18px] font-bold">Add Evento</span>
          </Button>
        </div>
      }
    >
      <div className="flex gap-[32px]">
        {/* Main Calendar - Left Side */}
        <div className="bg-[#15203C] rounded-[20px] p-[30px] flex flex-col gap-[40px] w-[1010px]">
          <CalendarNavigation
            currentMonth={currentMonth}
            currentYear={currentYear}
            onPrevMonth={handlePrevMonth}
            onToday={handleToday}
            onNextMonth={handleNextMonth}
          />

          <MonthlyCalendarGrid
            currentDate={currentDate}
            events={events}
            onEventClick={handleEventClick}
          />

          {/* Divider */}
          <div className="h-0 border-b border-gray-800/60" />

          {/* Próximos Eventos List */}
          <div className="flex flex-col gap-[20px]">
            <p className="text-[14px] font-bold text-white">Próximos Eventos:</p>

            <div className="flex flex-col gap-[20px]">
              {upcomingEvents.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">
                  Nenhum evento agendado
                </p>
              ) : (
                upcomingEvents.slice(0, 3).map((event, index) => (
                  <div key={event.id} className="flex flex-col gap-[10px]">
                    <div className="flex items-center gap-[10px]">
                      <div className="size-[7px] rounded-full bg-[#7846FF] flex-shrink-0" />
                      <p className="text-[14px] font-bold text-white">{event.title}</p>
                    </div>
                    <p className="text-[12px] text-white">
                      <span className="font-bold text-[#A085FF]">
                        {new Intl.DateTimeFormat("pt-BR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }).format(new Date(event.date))}
                      </span>
                      {` - ${event.startTime} - ${event.endTime}`}
                    </p>
                    {index < upcomingEvents.length - 1 && index < 2 && (
                      <div className="h-0 border-b border-gray-800/60 mt-[10px]" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Calendar Widget - Right Sidebar */}
        <div className="w-[490px]">
          <CalendarWidget
            events={upcomingEvents}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
        </div>
      </div>

      {/* Dialogs */}
      <AddEventDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddEvent}
      />

      <EditEventDialog
        open={editDialogOpen}
        event={selectedEvent}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedEvent(null);
        }}
        onUpdate={handleUpdateEvent}
        onDelete={handleDeleteEvent}
      />
    </AppContainer>
  );
}

export default Calendario;
